import React, { FunctionComponent, PropsWithChildren, RefObject, useEffect, useRef } from "react";
import { connect, MapStateToProps } from "react-redux";

import { ButtonBackgroundColor, Divider, IconButton } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { LOC_ADD, LOC_COPY, LOC_MOVE_DOWN, LOC_MOVE_UP, LOC_REMOVE } from "UI/UI.React/workflowEditor/locKeys";
import { isWorkflowReadOnly } from "../../../selectors";
import { WorkflowEditorProps } from "../../../types";
import { SideListFrameItem } from "./SideListFrameItem";

type SidePanelComponentProps = PropsWithChildren <{
    readonly selectedItemIndex: number;
    readonly leftFrameWidth?: number;
    readonly rightFrameWidth?: number;
    readonly itemsSource: readonly SidePanelItemsSource[];
    readonly isWorkflowReadOnly: boolean;
    readonly onAddItemClick: () => void;
    readonly onRemoveItemClick: () => void;
    readonly onCopyItemClick: () => void;
    readonly onOrderChange?: (newIndex: number) => void;
    readonly addText: string;
    readonly removeText: string;
    readonly copyText: string;
    readonly moveUpText: string;
    readonly moveDownText: string;
}>;

export type SidePanelItemsSource = {
    readonly headerName?: string;
    readonly headerLocKey?: string;
    readonly description1: string;
    readonly description2?: string;
    readonly description3?: string;
    readonly iconName?: string;
    readonly onItemClick: () => void;
};

const classNames = bemClassNames("side-list-frame-component");
const frameStyle = classNames();
const leftFrame = classNames("left-side-frame");
const rightFrame = classNames("right-side-frame");
const itemsContainer = classNames("items-container");
const buttonsContainer = classNames("buttons-container");

export const SideListFrameComponent: FunctionComponent<SidePanelComponentProps> = ({
    children,
    itemsSource,
    leftFrameWidth = 40,
    rightFrameWidth = 60,
    selectedItemIndex,
    isWorkflowReadOnly,
    onAddItemClick,
    onRemoveItemClick,
    onCopyItemClick,
    onOrderChange,
    addText,
    removeText,
    copyText,
    moveUpText,
    moveDownText,
}) => {
    const hasSourceNoItem = itemsSource.length === 0;
    const isMoveUpDisabled = selectedItemIndex === 0 || hasSourceNoItem;
    const isMoveDownDisabled = selectedItemIndex === itemsSource.length - 1 || hasSourceNoItem;

    const sideListItemRef = useRef<HTMLDivElement>(null);

    const autoScroll = (ref: RefObject<HTMLDivElement>) => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });

    useEffect(() => {
        autoScroll(sideListItemRef);
    }, [itemsSource]);

    return (
        <div className={frameStyle}>
            <div className={leftFrame} style={{ width: `${leftFrameWidth}%` }}>
                <div className={buttonsContainer}>
                    <div>
                        <IconButton iconCode={IconCode.uiPlus} outline={false} disabled={isWorkflowReadOnly} onClick={onAddItemClick} title={addText} backgroundColor={ButtonBackgroundColor.transparent} />
                        <IconButton iconCode={IconCode.delete} outline={false} disabled={hasSourceNoItem || isWorkflowReadOnly} onClick={onRemoveItemClick} title={removeText} backgroundColor={ButtonBackgroundColor.transparent} />
                        <IconButton iconCode={IconCode.docCopy} outline={false} disabled={hasSourceNoItem || isWorkflowReadOnly} onClick={onCopyItemClick} title={copyText} backgroundColor={ButtonBackgroundColor.transparent} />
                    </div>
                    {onOrderChange && <div>
                        <IconButton iconCode={IconCode.uiArrowTop} outline={false} disabled={isMoveUpDisabled || isWorkflowReadOnly} onClick={() => onOrderChange(selectedItemIndex - 1)} title={moveUpText} backgroundColor={ButtonBackgroundColor.transparent} />
                        <IconButton iconCode={IconCode.uiArrowBottom} outline={false} disabled={isMoveDownDisabled || isWorkflowReadOnly} onClick={() => onOrderChange(selectedItemIndex + 1)} title={moveDownText} backgroundColor={ButtonBackgroundColor.transparent} />
                    </div>}
                </div>
                <Divider />
                <div className={itemsContainer}>
                    {itemsSource.map(({ headerName = "", headerLocKey = "", description1, description2, description3, onItemClick, iconName }, index) =>
                        <SideListFrameItem ref={index === selectedItemIndex ? sideListItemRef : null} key={`frmItm${index}`} isSelected={index === selectedItemIndex} {...{ headerName, headerLocKey, description1, description2, description3, iconName, onItemClick }} />,
                    )}
                </div>
            </div>
            <div className={rightFrame} style={{ width: `${rightFrameWidth}%` }}>
                {children}
            </div>
        </div>
    );
};

type SideListFrameItemProps = Pick<SidePanelComponentProps, "isWorkflowReadOnly" | "addText" | "removeText" | "copyText" | "moveUpText" | "moveDownText">;

const mapStateToProps: MapStateToProps<SideListFrameItemProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    isWorkflowReadOnly: isWorkflowReadOnly(state, hostKey),
    addText: getPrimaryText(state, LOC_ADD) ?? "Přidat",
    removeText: getPrimaryText(state, LOC_REMOVE) ?? "Odebrat",
    copyText: getPrimaryText(state, LOC_COPY) ?? "Kopírovat",
    moveUpText: getPrimaryText(state, LOC_MOVE_UP) ?? "Posunout nahoru",
    moveDownText: getPrimaryText(state, LOC_MOVE_DOWN) ?? "Posunout dolů",
});

export const SideListFrame = connect(mapStateToProps)(SideListFrameComponent);
